<script setup lang="ts">
import TeemillLink from '@/components/TeemillLink.vue';
import { useAuth } from '@/services/auth';
import { useProjects } from '@/services/projects';
import { useUser } from '@/services/user';
import { useUserStore } from '@/store/user';
import { useBreakpoints } from '@/utils/breakpoints';
import { projectType } from '@/utils/projectType';
import { faCloud, faCog, faPlusLarge, faRightFromBracket } from '@fortawesome/pro-light-svg-icons';
import {
  FontAwesomeIcon,
  TmlBubble,
  TmlButton,
  TmlDropdownContainer,
  TmlForm,
  TmlInput,
} from '@teemill/components';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';

import { usePlatform } from '@/services/platform';
import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons';
import TeemillProjectIcon from './TeemillProjectIcon.vue';
import TeemillThemeIcon from './TeemillThemeIcon.vue';

const breakpoints = useBreakpoints();
const smallerThanSm = breakpoints.smaller('sm');
const isDevelopment = import.meta.env.MODE === 'development';
const user = useUser();
const userStore = useUserStore();
const { project: activeProject, all: projects } = useProjects();
const router = useRouter();
const { active: platform } = usePlatform();
const accountMenuOpen = ref(false);

const onLogOut = (): void => {
  void user
    .logout()
    .catch(() => {})
    .finally(() => {
      useAuth().unAuthenticate();
      useUserStore().update(undefined);

      router.push('/login/');
    });
};

const filteredProjects = computed(() =>
  projects.filter(
    (project) => !search.value || project.name.toLowerCase().includes(search.value.toLowerCase()),
  ),
);

const search = ref('');

const showSearch = computed(() => projects.length > 5);
</script>
<template>
  <TmlDropdownContainer
    v-model:open="accountMenuOpen"
    align="left"
    max-height="610px"
    width="300px"
    offset="1rem"
    @update:open="search = ''"
  >
    <template #trigger>
      <TmlButton class="!rounded-full" @click="accountMenuOpen = !accountMenuOpen">
        <TmlBubble>
          <TeemillProjectIcon
            v-if="activeProject !== undefined"
            :project="activeProject"
            class="w-full h-full"
          />
          <FontAwesomeIcon v-else :icon="faCog" />
        </TmlBubble>
      </TmlButton>
    </template>
    <template #default="{ as }">
      <div
        class="sticky top-0 overflow-auto flex flex-col bg-[--tml-dropdown-background-color] z-10"
        :class="{ 'pb-1.5': !showSearch && as !== 'popup' }"
      >
        <h3 v-if="as === 'popup'" class="ml-4 my-3">Projects</h3>

        <TmlForm
          v-if="showSearch"
          class="flex items-center gap-1"
          :class="as === 'popup' ? 'pb-2 px-2' : 'p-2'"
        >
          <label for="search-messages" class="sr-only">Search</label>
          <TmlInput
            v-model="search"
            class="grow"
            name="search-projects"
            autocomplete="projects"
            placeholder="Search projects"
            autofocus
          >
            <template #before>
              <FontAwesomeIcon
                :icon="faMagnifyingGlass"
                class="ml-4 text-[--tml-input-border-color]"
              />
            </template>
          </TmlInput>
        </TmlForm>
      </div>

      <div v-if="activeProject" class="flex gap-2 px-2 pb-1 items-center !rounded-none">
        <TmlBubble class="shrink-0">
          <TeemillProjectIcon :project="activeProject" class="w-full h-full" />
        </TmlBubble>

        <div class="flex flex-col overflow-hidden font-bold">
          <span class="truncate leading-5">
            {{ activeProject.name }}
          </span>
        </div>
      </div>

      <TeemillLink
        v-for="project in projects.filter((project) => project.id !== activeProject?.id)"
        v-show="filteredProjects.includes(project)"
        :key="project.id"
        :to="{
          name: 'projects.switch',
          query: {
            project: project.id,
          },
        }"
        class="flex gap-2 px-2 py-1 first:pt-2"
      >
        <TmlBubble class="shrink-0">
          <TeemillProjectIcon :project="project" class="w-full h-full" />
        </TmlBubble>

        <div
          class="flex flex-col overflow-hidden"
          :class="{ 'font-bold': project.id === activeProject?.id }"
        >
          <span class="truncate leading-5">
            {{ project.name }}
          </span>
          <small>
            {{ projectType(project).name }}
            <span v-if="userStore.user?.isAdmin && project.platform?.name"
              >({{ project.platform.name }})</span
            >
          </small>
        </div>
      </TeemillLink>

      <TeemillLink
        v-if="platform?.public"
        class="p-2 pt-1 flex gap-2 items-center hover:!bg-[--tml-dropdown-action-hover-background-color]"
        :to="{ name: 'projects.create' }"
      >
        <TmlBubble>
          <FontAwesomeIcon :icon="faPlusLarge" />
        </TmlBubble>
        Create project
      </TeemillLink>

      <div class="sticky bottom-0 overflow-auto flex flex-col bg-[--tml-card-background-color]">
        <hr />

        <TmlButton
          :to="{
            name: 'dashboard',
          }"
          class="p-2 pb-1 flex items-center !text-[--tml-card-text-color] hover:bg-[--tml-card-hover-background-color] !rounded-none"
          @click="accountMenuOpen = false"
        >
          <TmlBubble class="mr-2">
            <FontAwesomeIcon :icon="faCloud" />
          </TmlBubble>
          My Account
        </TmlButton>
        <TeemillThemeIcon
          v-if="isDevelopment && smallerThanSm"
          class="flex items-center px-2 py-1 hover:bg-[--tml-card-hover-background-color] !rounded-none"
        >
          <span class="ml-2">Theme</span>
        </TeemillThemeIcon>
        <TmlButton
          class="flex items-center p-2 pt-1 hover:bg-[--tml-card-hover-background-color] !rounded-none"
          @click="onLogOut"
        >
          <TmlBubble class="mr-2">
            <FontAwesomeIcon :icon="faRightFromBracket" />
          </TmlBubble>
          Log out
        </TmlButton>
      </div>
    </template>
  </TmlDropdownContainer>
</template>
